import { autoinject, transient } from "aurelia-framework";
import { NotifyPropertyChangedBase } from "../../../../utilities/reflection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { Dashboard, DashboardWidget, DashboardWidgetCoordinates } from "../../../../models/dashboard/dashboard.models";
import { ItemSource, SourceType, DataGridColumn, DataType } from "../../../../models/queries/queries.models";
import { DashboardClientService } from "../../../../services/dashboard/dashboard-client.service";
import { I18N } from "aurelia-i18n";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../../constants/authentication/base-claims";

@autoinject
@transient()
export class CloneDashboardWidgetDialog extends NotifyPropertyChangedBase {

    //currentDashboardId is currently viewed dashboard in dashboards page
    private _currentDashboardId: number;
    private _currentDashboardWidgets: DashboardWidget[];
    //_userDashboards are the dashboards of the user in the dashboards page
    private _userDashboards: Dashboard[];
    private _clone: (dashboardId: number, isGlobal: boolean, coords: DashboardWidgetCoordinates) => void;
    private _widgetWidth: number;
    private _canAccessGlobalDashboards: boolean = false;
    private _availableDashboards: Dashboard[] = null;
    private _dashboardColumns: DataGridColumn[];
    public cloneToDifferentDashboard: boolean = false;
    public selectedDashboardId: number = 0;
    public dashboardsSource: ItemSource;

    constructor(private dialogClient: DialogClientService,
        private notificationService: NotificationClientService,
        private dashboardClientService: DashboardClientService,
        private userClaimsService: UserClaimsService,
        private i18N: I18N) {
        super();
        this._canAccessGlobalDashboards = this.userClaimsService.hasPermission(BaseClaims.ManageDashboards);
        this._dashboardColumns = [
            {
                sortOrder: 1,
                columnOrder: 1,
                name: 'name',
                dataType: DataType.Default,
                title: this.i18N.tr('name-text')
            },
            {
                sortOrder: 2,
                columnOrder: 2,
                name: 'isGlobal',
                dataType: DataType.Boolean,
                width: '100px',
                title: this.i18N.tr('is-global-text')
            }
        ];
    }

    private getEndCoordinates(dashboardId: number) {
        return new Promise<{ x: number, y: number }>((resolve) => {
            let lasty = 0, lastx = 0;
            const selectedDashboard = this._availableDashboards.filter(x => x.dashboardId == dashboardId)[0];

            const getCoordinatesFromSerializedData = () => {
                if (this._currentDashboardWidgets == null || this._currentDashboardWidgets.length <= 0) {
                    const coordinates: DashboardWidgetCoordinates = { x: 0, y: 0 }
                    resolve(coordinates);
                }

                lasty = Enumerable.from(this._currentDashboardWidgets).max(x => x.y);
                const tmpWidgets = Enumerable.from(this._currentDashboardWidgets).where(x => x.y == lasty);
                lastx = Enumerable.from(tmpWidgets).max(x => x.x);
                const lastWidget = Enumerable.from(tmpWidgets).firstOrDefault(x => x.x == lastx);
                lasty = lasty + lastWidget.height;
                lastx = lastx + lastWidget.width;
                lastx = lastx + this._widgetWidth >= 12 ? 0 : lastx;

                const coordinates: DashboardWidgetCoordinates = { x: lastx, y: lasty };
                resolve(coordinates);
            }

            if (this._currentDashboardWidgets == null || this._currentDashboardWidgets.length <= 0) {
                this.dashboardClientService.getWidgetsForDashboard(selectedDashboard.dashboardId).then(x => {
                    this._currentDashboardWidgets = x.dashboardWidgets;
                    return getCoordinatesFromSerializedData();
                });
            }
            else {
                return getCoordinatesFromSerializedData();
            }
        });
    }

    cancel() {
        this.dialogClient.hide();
    }

    load() {
        if (this._canAccessGlobalDashboards) {
            this.dashboardClientService.getGlobalDashboards().then(result => {
                const dashboards: Dashboard[] = result.dashboards.concat(this._userDashboards);
                //used to only show distinct dashboards for cases where global dashboards are found in user dashboards as well
                this._availableDashboards = Enumerable.from(dashboards).groupBy(x => x.id).select(x => x.first()).toArray();
                this.dashboardsSource = {
                    source: this._userDashboards.concat(result.dashboards),
                    sourceType: SourceType.Collection,
                    columns: this._dashboardColumns
                };
            });
        }
        else {
            this._availableDashboards = Enumerable.from(this._userDashboards).where(x => x.isReadonly == null || !x.isReadonly).toArray();
            this.dashboardsSource = {
                source: this._availableDashboards,
                sourceType: SourceType.Collection,
                columns: this._dashboardColumns
            };
        }
    }

    clone() {
        return new Promise<void>((resolve, reject) => {
            if (this.cloneToDifferentDashboard && this.selectedDashboardId == 0) {
                this.notificationService.show(this.i18N.tr('no-dashboard-selected-text'), this.i18N.tr('no-dashboard-selected-description'), NotificationType.Warning);
                reject();
                return;
            }
            else {
                let isGlobal: boolean = true;
                isGlobal = !Enumerable.from(this._userDashboards).any(x => x.dashboardId == this.selectedDashboardId);

                const dashboardId: number = this.cloneToDifferentDashboard == false ? this._currentDashboardId : this.selectedDashboardId;
                const global: boolean = this.cloneToDifferentDashboard == false ? false : isGlobal;

                this.getEndCoordinates(dashboardId).then(x => {
                    const coords: DashboardWidgetCoordinates = x;
                    this._clone(dashboardId, global, coords);
                    resolve();
                    this.dialogClient.hide();
                });
            }
        });
    }

    selectDashboard(event) {
        if (event.detail.item) {
            this.selectedDashboardId = event.detail.item.dashboardId;
        }
    }

    activate(model: any) {
        this._userDashboards = model.dashboards;
        this._currentDashboardWidgets = model._currentDashboardWidgets;
        this._clone = model.clone;
        this._currentDashboardId = model.currentDashboardId;
        this._widgetWidth = model.width;
        this.load();
    }
}
